import { useCallback, useEffect, useState } from 'react';
import { Card } from '@heroui/react';

import ActivityItem from '../../common/ActivityItem';
import Notice from '../../common/Notice';
import { LoadMore } from '../../common/LoadMore';
import { SkeletonCard } from '../../common/Skeleton';
import { listCollectionActivities } from '../../../services/activity.service';
import { ACTIVITY, messageOf } from '../../../i18n/el';

// Η ροή δραστηριότητας μιας συλλογής (ΛΑ-21, ΛΑ-23, ΜΛΑ-04).
//
// **Ένα συστατικό για τρεις χρήσεις**, και η δεύτερη είναι ολόκληρη απαίτηση: με
// `resource` περιορίζεται στο ιστορικό **ενός πόρου**, δηλαδή στην απόδοση
// επιμέλειας που ζητά η ΛΑ-23· με `task`, στο ιστορικό **ενός καθήκοντος**, μέσα
// στον διάλογό του. Δεν χρειάστηκε δεύτερη διαδρομή ούτε δεύτερο ερώτημα — μία
// παράμετρος πάνω στον ίδιο κατάλογο, με τον **ίδιο** έλεγχο ορατότητας.
//
// **Ο κατάλογος φορτώνεται με προσάρτηση** και όχι με αριθμημένες σελίδες: είναι
// κοντός και δεν φιλτράρεται, όπως τα σχόλια κάτω από πόρο. Η αρίθμηση θα ήταν
// βαρύτερη από το περιεχόμενο.
//
// **Καμία σήμανση ανάγνωσης εδώ.** Η ροή δείχνει τι έγινε στη συλλογή· η
// κατάσταση ανάγνωσης ανήκει στις ειδοποιήσεις, που απευθύνονται σε πρόσωπο.
// Γι' αυτό το `ActivityItem` δεν παίρνει `onMarkRead`: το ίδιο συστατικό είναι
// αναγνώσιμο εδώ και ενεργό εκεί.

export default function CollectionActivity({
    collectionId,
    resource = null,
    task = null,
    title = null,
    emptyMessage = null
}) {
    const [entries, setEntries] = useState([]);
    const [meta, setMeta] = useState(null);
    const [page, setPage] = useState(1);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        if (!collectionId) return;
        setLoading(true);
        setError('');
        try {
            const result = await listCollectionActivities(collectionId, {
                page,
                resource: resource ?? undefined,
                task: task ?? undefined
            });
            // Η προσάρτηση αντικαθιστά στην πρώτη σελίδα και προσθέτει στις
            // επόμενες: αλλιώς μια επαναφόρτωση θα διπλασίαζε τον κατάλογο.
            setEntries((previous) => (page === 1 ? result.data : [...previous, ...result.data]));
            setMeta(result.meta);
        } catch (caught) {
            setError(messageOf(caught));
        } finally {
            setLoading(false);
        }
    }, [collectionId, page, resource, task]);

    useEffect(() => {
        load();
    }, [load]);

    // Αλλαγή πόρου ή συλλογής ξεκινά από την πρώτη σελίδα. Χωρίς αυτό, το άνοιγμα
    // δεύτερου πόρου θα ζητούσε τη σελίδα 3 του δικού του ιστορικού.
    useEffect(() => {
        setPage(1);
        setEntries([]);
    }, [collectionId, resource, task]);

    return (
        <Card>
            <Card.Header>
                <Card.Title>{title ?? ACTIVITY.feedTitle}</Card.Title>
            </Card.Header>

            <Card.Content className="flex flex-col gap-3">
                {/* Το σφάλμα αποδίδεται μέσα στην κάρτα, δίπλα στο περιεχόμενο
                    που λείπει, και όχι σε κοινό σημείο της σελίδας. */}
                {error && <Notice status="danger" title={ACTIVITY.feedError}>{error}</Notice>}

                {isLoading && entries.length === 0 && <SkeletonCard />}

                {!isLoading && !error && entries.length === 0 && (
                    <p className="py-8 text-center text-sm text-muted">
                        {emptyMessage ?? ACTIVITY.feedEmpty}
                    </p>
                )}

                {entries.length > 0 && (
                    <ul className="flex flex-col gap-2">
                        {entries.map((entry) => (
                            <ActivityItem key={entry.id} entry={entry} />
                        ))}
                    </ul>
                )}

                <LoadMore
                    loaded={entries.length}
                    meta={meta}
                    isLoading={isLoading}
                    onLoadMore={() => setPage((current) => current + 1)}
                    noun="ενέργειες"
                />
            </Card.Content>
        </Card>
    );
}
